import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Search as SearchIcon, Sparkles, Globe2 } from "lucide-react";
import { toast } from "sonner";

function verdictColor(v = "") {
  if (v.includes("Critical")) return "bg-red-50 text-red-700 border-red-200";
  if (v.includes("High")) return "bg-orange-50 text-orange-700 border-orange-200";
  if (v.includes("Moderate")) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-green-50 text-green-700 border-green-200";
}

export default function Search() {
  const [q, setQ] = useState("");
  const [ipType, setIpType] = useState("");
  const [country, setCountry] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sim, setSim] = useState({}); // map target -> result
  const [simLoading, setSimLoading] = useState({});

  const run = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const params = { q };
      if (ipType) params.ip_type = ipType;
      if (country) params.country = country;
      const { data } = await api.get("/search", { params });
      setRows(data);
      setSim({});
      if (data.length === 0) toast.info("No matches found");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const runSimilarity = async (row) => {
    if (rows.length < 2) {
      toast.error("Need at least 2 results to compare");
      return;
    }
    const others = rows.filter((r) => r.id !== row.id);
    const candidate = others[0];
    setSimLoading((s) => ({ ...s, [row.id]: true }));
    try {
      const { data } = await api.post("/ai/similarity", {
        source_id: row.id,
        candidate_id: candidate.id,
      });
      setSim((m) => ({ ...m, [row.id]: { ...data, against: candidate.title } }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSimLoading((s) => ({ ...s, [row.id]: false }));
    }
  };

  return (
    <div className="p-6 md:p-8 fade-in-up" data-testid="search-page">
      <div className="mb-8">
        <div className="eyebrow">Global IP Search</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-1">
          Prior-Art & Brand Discovery
        </h1>
        <p className="text-slate-600 mt-2 text-sm md:text-base">
          Search a global index of patents, trademarks, and copyrights. Run AI similarity analysis on any hit.
        </p>
      </div>

      <form onSubmit={run} className="bengal-card p-5 mb-6 flex flex-wrap gap-3 items-center" data-testid="search-form">
        <div className="flex items-center gap-2 flex-1 min-w-[280px] border border-slate-200 rounded-sm px-3 bg-slate-50">
          <SearchIcon size={16} className="text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, abstract, filing number…"
            data-testid="search-input"
            className="flex-1 text-sm focus:outline-none bg-transparent py-2"
          />
        </div>
        <select value={ipType} onChange={(e) => setIpType(e.target.value)} className="border border-slate-300 rounded-sm text-sm px-3 py-2 bg-white" data-testid="search-type">
          <option value="">All types</option>
          <option value="patent">Patents</option>
          <option value="trademark">Trademarks</option>
          <option value="copyright">Copyrights</option>
        </select>
        <input
          value={country}
          onChange={(e) => setCountry(e.target.value.toUpperCase())}
          placeholder="Country (US, EU…)"
          data-testid="search-country"
          className="w-32 border border-slate-300 rounded-sm text-sm px-3 py-2 uppercase font-mono"
        />
        <button type="submit" className="bengal-btn-primary text-sm" data-testid="search-btn">
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {rows.length > 0 && (
        <div className="eyebrow text-slate-500 mb-3">{rows.length} result{rows.length !== 1 ? "s" : ""}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <div key={r.id} className="bengal-card p-5" data-testid={`search-result-${r.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="eyebrow capitalize text-slate-500">{r.ip_type}</span>
                  <span className="eyebrow text-slate-300">·</span>
                  <span className="eyebrow font-mono text-slate-500 flex items-center gap-1">
                    <Globe2 size={10} /> {r.country}
                  </span>
                  {r.is_own && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-red-50 text-[#B33A3A] border-red-200">
                      Own
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-xl font-medium text-slate-900 truncate">{r.title}</h3>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border shrink-0 ${
                r.status === "granted" || r.status === "registered"
                  ? "bg-green-50 text-green-800 border-green-200"
                  : "bg-amber-50 text-amber-800 border-amber-200"
              }`}>
                {r.status}
              </span>
            </div>
            {r.abstract && <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-3">{r.abstract}</p>}
            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <div className="text-xs text-slate-500 font-mono">{r.filing_number}</div>
              <button
                onClick={() => runSimilarity(r)}
                disabled={simLoading[r.id]}
                data-testid={`similarity-btn-${r.id}`}
                className="text-xs text-[#B33A3A] hover:underline flex items-center gap-1.5 disabled:opacity-50"
              >
                <Sparkles size={12} /> {simLoading[r.id] ? "Analyzing…" : "AI similarity"}
              </button>
            </div>
            {sim[r.id] && (
              <div className={`mt-3 p-3 rounded-sm border ${verdictColor(sim[r.id].verdict)}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium">{sim[r.id].verdict}</span>
                  <span className="font-mono">Score: {sim[r.id].score}/100</span>
                </div>
                <div className="text-xs opacity-75 mb-1">vs. <em>{sim[r.id].against}</em></div>
                <p className="text-xs leading-relaxed">{sim[r.id].rationale}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {!loading && rows.length === 0 && (
        <div className="bengal-card p-12 text-center">
          <Globe2 size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-slate-500 text-sm">Search the global IP index above.</div>
        </div>
      )}
    </div>
  );
}
